import { memo, useEffect, useCallback, useState } from "react";
import Form from "../../components/Form.js";
import Table from "../../components/Table.js";
import { useSnackbar } from "../../utils/index.js";
import Spinner from "../../components/Spinner.js";
import { Box, Typography, Grid2, Button } from "@mui/material";
import { getOwners, getAvailableScrapieLambs, createLambScrapie } from "../../api/index.js";
import useSelectStyles from "../../components/styles/Select.js";
import useListStyles from "../../components/styles/List.js";
import {
    lambSlugHeaderNoFilterable,
    damCodeHeaderNoClick,
    sireCodeHeaderNoClick,
    sexHeaderNoFilter,
    birthDateHeaderNotFilterable,
    lambStatusHeader,
} from "../../components/TableHeaders.js";

const CreateLambScrapie = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [sampleDate, setSampleDate] = useState(null);
    const [resultDate, setResultDate] = useState(null);
    const [fetchedLambs, setFetchedLambs] = useState([]);
    const [selectedLamb, setSelectedLamb] = useState(null);
    const [scrapieCds, setScrapieCds] = useState([]);
    const [scrapieCodesDropdownValues, setScrapieCodesDropdownValues] = useState([]);
    const [sampleStts, setSampleStts] = useState([]);
    const [sampleStatesDropdownValues, setSampleStatesDropdownValue] = useState([]);

    const selectClasses = useSelectStyles();
    const listClasses = useListStyles();
    const { success, error } = useSnackbar();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { owners } = await getOwners();
            setFetchedOwners(owners);
            setOwnersDropdownValues(
                owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                }))
            );
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }, [error])

    useEffect(() => {
        fetchData();
    }, []);

    const selectOwnerDatesFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "date-picker",
            id: "startLambingDate",
            type: "desktop",
            label: "Έναρξη περιόδου τοκετών",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "date-picker",
            id: "endLambingDate",
            type: "desktop",
            label: "Τέλος περιόδου τοκετών",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "date-picker",
            id: "testDate",
            type: "desktop",
            label: "Ηερομηνία Δειγματοληψίας",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "date-picker",
            id: "resultDate",
            type: "desktop",
            label: "Ημερομηνία Αποτελεσμάτων",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const selectLambFormContent = [

        {
            customType: "input",
            id: "lamb",
            type: "text",
            placeholder: "Κωδικός Αμνού",
            value: "",
            disabled: false,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];


    const scrapieFormContent = [
        {
            customType: "input",
            id: "sampleNumber",
            type: "text",
            placeholder: "Αριθμός Δείγματος",
            value: "",
            disabled: false,
        },
        {
            customType: "dropdown",
            id: "sampleState",
            items: sampleStatesDropdownValues,
            placeholder: "Κατάσταση Δείγματος",
            required: false,
            defaultValue: sampleStts[4]?.sampleStateName,
        },
        {
            customType: "dropdown",
            id: "firstResult",
            items: scrapieCodesDropdownValues,
            placeholder: "Πρώτο Αποτέλεσμα",
            required: true,
            defaultValue: scrapieCds[0]?.scrapieName,
        },
        {
            customType: "dropdown",
            id: "secondResult",
            items: scrapieCodesDropdownValues,
            placeholder: "Δεύτερο Αποτέλεσμα",
            required: true,
            defaultValue: scrapieCds[0]?.scrapieName,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const lambsTableColumns = [
        lambSlugHeaderNoFilterable,
        damCodeHeaderNoClick,
        sireCodeHeaderNoClick,
        sexHeaderNoFilter,
        birthDateHeaderNotFilterable,
        lambStatusHeader,
    ];

    const handleOwnerDatesSelect = async (values) => {
        setIsLoading(true);
        const selectedOwnerObj = fetchedOwners.find(
            (owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner
        );
        setSelectedOwner(selectedOwnerObj);
        setSampleDate(values.testDate);
        setResultDate(values.resultDate);
        const { success: scs, lambs, scrapieCodes, sampleStates, message } = await getAvailableScrapieLambs(
            selectedOwnerObj?._id,
            values.startLambingDate,
            values.endLambingDate,
            values.testDate,
            values.resultDate
        );
        if (scs) {
            setFetchedLambs(lambs);
            setScrapieCds(scrapieCodes);
            setSampleStts(sampleStates);
            setScrapieCodesDropdownValues(
                scrapieCodes.map((code) => ({
                    value: code?.scrapieName,
                    text: code?.scrapieName,
                }))
            );
            setSampleStatesDropdownValue(
                sampleStates.map((state) => ({
                    value: state?.sampleStateName,
                    text: state?.sampleStateName,
                }))
            );
            setStep(2);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    const handleLambSelect = (values) => {
        const selectedLambObj = fetchedLambs.find((lamb) => lamb.lambSlug === values.lamb);
        setSelectedLamb(selectedLambObj);
        setStep(3);
    };

    const handleScrapieSubmit = async (values) => {
        setIsLoading(true);
        const ownerId = selectedOwner?._id;
        const lambSlug = selectedLamb?.lambSlug;
        const sampleCode = values.sampleNumber;
        const smplDate = sampleDate;
        const rsltDate = resultDate;
        const sampleState = sampleStts.find((state) => state?.sampleStateName === values.sampleState)?.sampleStateCode;
        const result1 = Number(scrapieCds.find((code) => code?.scrapieName === values.firstResult)?.scrapieCode);
        const result2 = Number(scrapieCds.find((code) => code?.scrapieName === values.secondResult)?.scrapieCode);

        const { success: scs, message } = await createLambScrapie(
            ownerId,
            lambSlug,
            sampleCode,
            smplDate,
            rsltDate,
            sampleState,
            result1,
            result2,
        );
        if (scs) {
            success(message);
            setFetchedLambs((prevLambs) => prevLambs.filter((lamb) => lamb.lambSlug !== selectedLamb?.lambSlug));
            setSelectedLamb(null);
            setStep(2);
        } else {
            error(message);
        }
        setIsLoading(false);
    }
    return (
        <>
            <Spinner open={isLoading} />
            {step === 1 && (
                <Box className={selectClasses.outerBox}>
                    <Box container flexDirection="column" className={selectClasses.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Επιλέξτε την Εκτροφή και τις ημερομηνίες που σας ενδιαφέρουν:
                        </Typography>
                        <Form
                            content={selectOwnerDatesFormContent}
                            onSubmit={handleOwnerDatesSelect}
                            toResetForm={true}
                        />
                    </Box>
                </Box>
            )}
            {step === 2 && (
                <Box>
                    <Form
                        content={selectLambFormContent}
                        onSubmit={handleLambSelect}
                        toResetForm={false}
                    />
                    {fetchedLambs.length > 0 && (
                        <Grid2
                            container
                            spacing={2}
                            sx={{
                                marginTop: "3rem",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                            }}>
                            <Grid2 item xs={12} md={12}>
                                <Typography variant="h6" className={listClasses.form_title} sx={{ display: "flex", justifyContent: "center" }}>
                                    {"Αμνοί για τους οποίους δεν υπάρχει καταγεγραμμένος έλεγχος SCRAPIE"}
                                </Typography>
                                <Table
                                    data={fetchedLambs}
                                    noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν αμνοί"}
                                    columns={lambsTableColumns}
                                    defaultSorted={[{ id: "birthDate", desc: true }]}
                                />
                            </Grid2>
                        </Grid2>
                    )}
                </Box>
            )}
            {step === 3 && selectedLamb && (
                <>
                    <Grid2
                        item
                        xs={12}
                        md={12}
                        sx={{
                            mt: "2rem",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="h6" className={listClasses.form_title} sx={{ display: "flex", justifyContent: "center" }}>
                            {"Στοιχεία Επιλεγμένου Ζώου"}
                        </Typography>
                        <Table
                            data={[selectedLamb]}
                            noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν ζώα"}
                            columns={lambsTableColumns.slice(0, 5)}
                            defaultSorted={[{ id: "birthDate", desc: true }]}
                            showPageSizeOptions={false}
                        />
                    </Grid2>
                    <Box className={selectClasses.outerBox} sx={{ marginTop: "-5rem" }}>
                        <Box container flexDirection="column" className={selectClasses.innerBox}>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Εισαγωγή Στοιχείων SCARPIE"}
                            </Typography>
                            <Form
                                content={scrapieFormContent}
                                onSubmit={handleScrapieSubmit}
                                toResetForm={true}
                            />
                        </Box>
                    </Box>
                </>
            )}
            {step !== 1 && (
                <Box sx={{ width: "100%", marginTop: step === 2 ? "3rem" : "-10rem", display: "flex", justifyContent: "center" }}>
                    <Button variant="outlined" color="secondary" onClick={() => setStep(step - 1)}>
                        Πίσω
                    </Button>
                </Box>
            )}
        </>
    )
}

export default memo(CreateLambScrapie);